import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, or, orderBy, limit, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { HazardAlert } from '../types';

// Types
interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'ticket' | 'payment' | 'system' | 'hazard';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  createdAt: any;
  toUserId?: string;
  toUserRole?: string;
  imageUrl?: string;
  data?: any;
}

interface CombinedNotification extends Notification {
  source: 'notification' | 'hazard';
}

const EnhancedNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<CombinedNotification[]>([]);
  const [hazardAlerts, setHazardAlerts] = useState<HazardAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<CombinedNotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch notifications and hazard alerts
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    setIsLoading(true);
    setError(null);

    // Fetch regular notifications
    const notificationsQuery = query(
      collection(db, 'notifications'),
      or(
        where('toUserId', '==', user.uid),
        where('toUserRole', '==', 'viewer'),
        where('toUserRole', '==', null)
      ),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    // Fetch hazard alerts
    const hazardsQuery = query(
      collection(db, 'hazardAlerts'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationsData: CombinedNotification[] = [];
        let unread = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const notification: CombinedNotification = {
            id: doc.id,
            title: data.title || '',
            message: data.message || '',
            type: data.type || 'system',
            priority: data.priority || 'medium',
            isRead: data.isRead || false,
            createdAt: data.createdAt,
            toUserId: data.toUserId,
            toUserRole: data.toUserRole,
            imageUrl: data.imageUrl,
            data: data.data || {},
            source: 'notification'
          };

          notificationsData.push(notification);
          if (!notification.isRead) unread++;
        });

        setNotifications(notificationsData);
        setUnreadCount(unread);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching notifications:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    const unsubscribeHazards = onSnapshot(
      hazardsQuery,
      (snapshot) => {
        const hazardsData: HazardAlert[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          const hazard: HazardAlert = {
            id: doc.id,
            title: data.title || '',
            description: data.description || '',
            hazardType: data.hazardType || 'other',
            coordinate: data.coordinate || { latitude: 0, longitude: 0 },
            island: data.island || 'St. Kitts',
            severity: data.severity || 'medium',
            isActive: data.isActive || true,
            createdAt: data.createdAt,
          };
          hazardsData.push(hazard);
        });

        setHazardAlerts(hazardsData);
      },
      (err) => {
        console.error('Error fetching hazard alerts:', err);
      }
    );

    return () => {
      unsubscribeNotifications();
      unsubscribeHazards();
    };
  }, []);

  // Combine notifications and hazard alerts
  const allNotifications = useMemo(() => {
    const hazardNotifications: CombinedNotification[] = hazardAlerts.map(hazard => ({
      id: hazard.id,
      title: hazard.title,
      message: hazard.description,
      type: 'hazard' as const,
      priority: hazard.severity === 'high' ? 'high' : 
               hazard.severity === 'medium' ? 'medium' : 'low',
      isRead: false, // Hazards are always considered "unread" for visibility
      createdAt: hazard.createdAt,
      data: {
        hazardType: hazard.hazardType,
        island: hazard.island,
        coordinate: hazard.coordinate,
        severity: hazard.severity
      },
      source: 'hazard' as const
    }));

    return [...notifications, ...hazardNotifications].sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
  }, [notifications, hazardAlerts]);

  // Mark notification as read
  const markAsRead = async (notificationId: string, source: 'notification' | 'hazard') => {
    if (source === 'notification') {
      try {
        await updateDoc(doc(db, 'notifications', notificationId), {
          isRead: true,
        });
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
    // Hazard alerts don't need to be marked as read in the database
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    const promises = unreadNotifications.map(n => markAsRead(n.id, 'notification'));
    
    try {
      await Promise.all(promises);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get notification icon
  const getNotificationIcon = (type: string, priority?: string) => {
    if (priority === 'urgent') return '🚨';
    if (priority === 'high') return '⚠️';
    
    switch (type) {
      case 'ticket': return '🎫';
      case 'payment': return '💳';
      case 'hazard': return '⚠️';
      case 'system': return '🔔';
      default: return '📢';
    }
  };

  // Get priority color
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ticket': return 'bg-blue-100 text-blue-800';
      case 'payment': return 'bg-green-100 text-green-800';
      case 'hazard': return 'bg-red-100 text-red-800';
      case 'system': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading && notifications.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 mt-1">
                {unreadCount > 0 && `${unreadCount} unread notifications`}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Mark All Read
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {allNotifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-5xl mb-4">🔕</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
              <p className="text-gray-500">You're all caught up!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {allNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                    notification.isRead
                      ? 'bg-white border-gray-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type, notification.priority)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Title and badges */}
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {notification.title}
                            </h3>
                            {notification.priority && (
                              <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(notification.priority)}`}>
                                {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(notification.type)}`}>
                              {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                            </span>
                            {notification.source === 'hazard' && (
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
                                Hazard Alert
                              </span>
                            )}
                          </div>

                          {/* Message */}
                          <p className="text-gray-600 mb-2">{notification.message}</p>

                          {/* Image preview */}
                          {notification.imageUrl && (
                            <div className="mb-3">
                              <img
                                src={notification.imageUrl}
                                alt="Notification"
                                className="h-32 w-auto object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => {
                                  setSelectedNotification(notification);
                                  setShowDetailModal(true);
                                }}
                              />
                            </div>
                          )}

                          {/* Additional details */}
                          {notification.data && Object.keys(notification.data).length > 0 && (
                            <div className="text-sm text-gray-500 mb-2">
                              {notification.data.location && (
                                <p>📍 {notification.data.location}</p>
                              )}
                              {notification.data.amount && (
                                <p>💰 EC${notification.data.amount}</p>
                              )}
                              {notification.data.offenceType && (
                                <p>🎫 {notification.data.offenceType}</p>
                              )}
                              {notification.data.hazardType && (
                                <p>⚠️ {notification.data.hazardType}</p>
                              )}
                              {notification.data.island && (
                                <p>🏝️ {notification.data.island}</p>
                              )}
                            </div>
                          )}

                          {/* Timestamp */}
                          <div className="flex items-center text-sm text-gray-500">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatDate(notification.createdAt)}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col space-y-2 ml-4">
                          {!notification.isRead && notification.source === 'notification' && (
                            <button
                              onClick={() => markAsRead(notification.id, notification.source)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Mark as read
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedNotification(notification);
                              setShowDetailModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedNotification(null);
          }}
          onMarkAsRead={() => markAsRead(selectedNotification.id, selectedNotification.source)}
        />
      )}
    </div>
  );
};

// Notification Detail Modal Component
interface NotificationDetailModalProps {
  notification: CombinedNotification;
  isOpen: boolean;
  onClose: () => void;
  onMarkAsRead: () => void;
}

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  isOpen,
  onClose,
  onMarkAsRead
}) => {
  if (!isOpen) return null;

  const getNotificationIcon = (type: string, priority?: string) => {
    if (priority === 'urgent') return '🚨';
    if (priority === 'high') return '⚠️';
    
    switch (type) {
      case 'ticket': return '🎫';
      case 'payment': return '💳';
      case 'hazard': return '⚠️';
      case 'system': return '🔔';
      default: return '📢';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">{getNotificationIcon(notification.type, notification.priority)}</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{notification.title}</h2>
                <p className="text-sm text-gray-500">{formatDate(notification.createdAt)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Message */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Message</h3>
            <p className="text-gray-600">{notification.message}</p>
          </div>

          {/* Image */}
          {notification.imageUrl && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Attachment</h3>
              <img
                src={notification.imageUrl}
                alt="Notification attachment"
                className="w-full rounded-lg"
              />
            </div>
          )}

          {/* Additional Details */}
          {notification.data && Object.keys(notification.data).length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {notification.source === 'hazard' ? 'Hazard Details' : 'Additional Details'}
              </h3>
              <div className="space-y-2">
                {notification.source === 'hazard' ? (
                  <>
                    {notification.data.hazardType && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Hazard Type:</span>
                        <span className="text-gray-900 capitalize">{String(notification.data.hazardType)}</span>
                      </div>
                    )}
                    {notification.data.island && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Island:</span>
                        <span className="text-gray-900">{String(notification.data.island)}</span>
                      </div>
                    )}
                    {notification.data.severity && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Severity:</span>
                        <span className="text-gray-900 capitalize">{String(notification.data.severity)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  Object.entries(notification.data).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className="text-gray-900">{String(value)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            {!notification.isRead && notification.source === 'notification' && (
              <button
                onClick={() => {
                  onMarkAsRead();
                  onClose();
                }}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Mark as Read
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedNotifications;
